import React from 'react';
import { Col, Form, Row } from 'antd';
import CustomInput from '@/components/CustomInput';
import CustomSelect from '@/components/CustomSelect';
import CustomTitle from '@/components/CustomTitle';
import CommonFooter from '@/components/CommonFooter';
import type {
  ApiKeyPlacement,
  CredentialAuthType,
} from '@/apis/customerCredentials';
import { NAME_MAX_LENGTH } from '../credentialFormat';

const AUTH_TYPE_OPTIONS = [
  { value: 'API_KEY', label: 'API key' },
  { value: 'OAUTH_CLIENT_CREDENTIALS', label: 'OAuth (client credentials)' },
];

const API_KEY_PLACEMENT_OPTIONS = [
  { value: 'HEADER', label: 'Header' },
  { value: 'QUERY', label: 'Query parameter' },
];

export interface CredentialFormValues {
  name: string;
  auth_type: CredentialAuthType;
  secret: string;
  api_key_placement?: ApiKeyPlacement;
  api_key_param_name?: string;
  oauth_token_url?: string;
  oauth_client_id?: string;
  oauth_scopes?: string;
}

interface CredentialFormProps {
  form: import('antd').FormInstance<CredentialFormValues>;
  onFinish: (values: CredentialFormValues) => void;
  onCancel: () => void;
  saving: boolean;
  /** Authentication type selected when the form opens; API key by default. */
  defaultAuthType?: CredentialAuthType;
}

const CredentialForm: React.FC<CredentialFormProps> = ({
  form,
  onFinish,
  onCancel,
  saving,
  defaultAuthType = 'API_KEY',
}) => {
  const authType = Form.useWatch('auth_type', form);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout='vertical'
      preserve={false}
      initialValues={{
        auth_type: defaultAuthType,
        api_key_placement: 'HEADER',
      }}
      className='credential-form'
    >
      <div className='custom-scrollbar sensor-credential-scroll'>
        <Row gutter={[16, 14]}>
          <Col span={24}>
            <CustomTitle
              text='New credential'
              fontSize={20}
              fontWeight='700'
              color='var(--text-primary)'
              marginBottom={0}
            />
          </Col>
          <Col span={24}>
            <Form.Item
              label='Name'
              name='name'
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Please enter a name',
                },
                {
                  max: NAME_MAX_LENGTH,
                  message: `Name cannot exceed ${NAME_MAX_LENGTH} characters`,
                },
              ]}
            >
              <CustomInput
                placeholder='e.g. World Courier API key'
                bgColor='var(--light-gray)'
                textColor='var(--text-primary)'
                height='36px'
                borderRadius='8px'
                border='none'
                maxLength={NAME_MAX_LENGTH}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label='Authentication type'
              name='auth_type'
              rules={[{ required: true, message: 'Please select a type' }]}
            >
              <CustomSelect
                bgColor='var(--light-gray)'
                textColor='var(--text-primary)'
                height='36px'
                borderRadius='8px'
                border='none'
                options={AUTH_TYPE_OPTIONS}
              />
            </Form.Item>
          </Col>

          {authType === 'API_KEY' && (
            <>
              <Col span={24}>
                <Form.Item
                  label='Placement'
                  name='api_key_placement'
                  rules={[
                    {
                      required: true,
                      message: 'Please select where the key goes',
                    },
                  ]}
                >
                  <CustomSelect
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    height='36px'
                    borderRadius='8px'
                    border='none'
                    options={API_KEY_PLACEMENT_OPTIONS}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label='Header or query parameter name'
                  name='api_key_param_name'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter a name, e.g. X-API-Key',
                    },
                  ]}
                >
                  <CustomInput
                    placeholder='X-API-Key'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    height='36px'
                    borderRadius='8px'
                    border='none'
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label='API key'
                  name='secret'
                  rules={[
                    { required: true, message: 'Please enter the API key' },
                  ]}
                >
                  <CustomInput
                    type='password'
                    placeholder='Paste the API key'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    height='36px'
                    borderRadius='8px'
                    border='none'
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {authType === 'OAUTH_CLIENT_CREDENTIALS' && (
            <>
              <Col span={24}>
                <Form.Item
                  label='Token URL'
                  name='oauth_token_url'
                  rules={[
                    { required: true, message: 'Please enter the token URL' },
                    { type: 'url', message: 'Must be a valid URL' },
                  ]}
                >
                  <CustomInput
                    placeholder='https://provider.example.com/oauth/token'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    height='36px'
                    borderRadius='8px'
                    border='none'
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label='Client ID'
                  name='oauth_client_id'
                  rules={[
                    { required: true, message: 'Please enter the client ID' },
                  ]}
                >
                  <CustomInput
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    height='36px'
                    borderRadius='8px'
                    border='none'
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label='Scopes' name='oauth_scopes'>
                  <CustomInput
                    placeholder='space-separated, optional'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    height='36px'
                    borderRadius='8px'
                    border='none'
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  label='Client secret'
                  name='secret'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter the client secret',
                    },
                  ]}
                >
                  <CustomInput
                    type='password'
                    placeholder='Paste the client secret'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-primary)'
                    height='36px'
                    borderRadius='8px'
                    border='none'
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </div>

      <div className='sensor-drawer-footer'>
        <CommonFooter
          text={saving ? 'Saving...' : 'Save credential'}
          onSave={() => form.submit()}
          handleCancel={onCancel}
        />
      </div>
    </Form>
  );
};

export default CredentialForm;
